import { UzButton, UzCard, UzDatePicker, UzInput } from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React from 'react';

import { emptyMulticityRow, type MulticityRow } from './invoice-form.helpers';

interface FlightMulticityGridProps {
  rows: MulticityRow[];
  onChange: (rows: MulticityRow[]) => void;
  showErrors: boolean;
  invalid: boolean;
}

/** Section 5 - Flight multicity legs (shown only for MultiCity fares). */
export const FlightMulticityGrid = ({
  rows,
  onChange,
  showErrors,
  invalid,
}: FlightMulticityGridProps): React.JSX.Element => {
  const update = (idx: number, patch: Partial<MulticityRow>): void =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  return (
    <UzCard
      title="Flight multicity"
      description="One row per leg."
      actions={
        <UzButton
          variant="outline"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => onChange([...rows, emptyMulticityRow()])}
        >
          Add leg
        </UzButton>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">
          {showErrors && invalid ? (
            <span className="text-red-600">Add at least one leg for a MultiCity fare.</span>
          ) : (
            'No legs added.'
          )}
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div key={idx} className="grid items-start gap-3 md:grid-cols-[1fr_1fr_180px_auto]">
              <UzInput
                label={idx === 0 ? 'Departure city' : undefined}
                value={row.departureCity}
                onChange={(e) => update(idx, { departureCity: e.target.value })}
              />
              <UzInput
                label={idx === 0 ? 'Destination city' : undefined}
                value={row.destinationCity}
                onChange={(e) => update(idx, { destinationCity: e.target.value })}
              />
              <UzDatePicker
                label={idx === 0 ? 'Departure date' : undefined}
                value={row.departureDate}
                onValueChange={(v) => update(idx, { departureDate: v })}
              />
              <div className={idx === 0 ? 'md:pt-6' : ''}>
                <UzButton
                  variant="ghost"
                  size="sm"
                  aria-label="Remove leg"
                  onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="h-4 w-4" />
                </UzButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </UzCard>
  );
};
